import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { SimpleModal } from '@livable/saas-react-components';
import { Alert, Button, FormControlLabel, MenuItem, RadioGroup, Switch, TextField, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { api } from 'src/api';
import { useCurrentSubscriptionTier } from 'src/components/SubscriptionTier';
import { useStore } from 'src/stores';
import { announceSetupChange, NO_OPTIONS } from 'src/stores/utilitySetups/companySetupsRead';
import { modelSetupFor, setupTypes } from './companySetups';
import { IntakeAddress } from './IntakeAddress';
import { MailCard, messageOf, Needs, sameId, SourceCard, Tip } from './parts';
import { apPushChoice, FREQUENCY_CHOICES, MAX_SETUP_NAME_LENGTH, newSetupDraftFrom, newSetupMethods, SOURCE_CHOICES, sourceChoice, SQUARE_FOOTAGE_METHODS } from './setupEditor';
import { frequencyLabel, methodLabel, SETUP_TIPS } from './setupSummary';
import useStyles from './styles';

// A new setup holds nothing yet, so every source and AP Push the plan doesn't include is locked.
const NOTHING_HELD = {
  billSource: null,
  isApPushEnabled: false
};

/**
 * New setup (LB-6193, PRD V3 FR-2): a company setup for a utility type the company already has setups of, which no
 * utility follows until one is linked to it. It's how a utility moves between RUBS and Sub-meter, which no edit can.
 * It starts from the type's most-used setup, so a new setup differs only where it's changed. Renders nothing while
 * isBillingOptionsEnabled is off.
 *
 * `setups` are the company's, archived ones included; `typeId` is the type it opens on. `onCreated(setup, name)` hears
 * the setup made, once every screen has been told to read again.
 */
export const UtilitySetupCreator = observer(_ref => {
  var _useCurrentSubscripti, _useCurrentSubscripti2;
  let companyId = _ref.companyId,
    setups = _ref.setups,
    openOnType = _ref.typeId,
    open = _ref.open,
    onClose = _ref.onClose,
    onCreated = _ref.onCreated;
  const classes = useStyles();
  const store = useStore();
  const enabled = !!store.account.featureFlagsStore.getIsBillingOptionsEnabled;
  const isAppfolio = !!store.company.companyFeatureStore.isAppfolio;
  const occupantsOptIn = !!store.company.companyFeatureStore.enableAllocationByNumberOfOccupants;
  const allows = (_useCurrentSubscripti = (_useCurrentSubscripti2 = useCurrentSubscriptionTier()) === null || _useCurrentSubscripti2 === void 0 ? void 0 : _useCurrentSubscripti2.allows) !== null && _useCurrentSubscripti !== void 0 ? _useCurrentSubscripti : null;
  const all = setups !== null && setups !== void 0 ? setups : [];
  const types = setupTypes(all);
  const methods = newSetupMethods(occupantsOptIn);
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    typeId = _useState2[0],
    setTypeId = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    name = _useState4[0],
    setName = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    nameTouched = _useState6[0],
    setNameTouched = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    draft = _useState8[0],
    setDraft = _useState8[1];
  // The setup the values came from: a read again of the list beside the dialog can change which one is most-used.
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    startedFrom = _useState0[0],
    setStartedFrom = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    saving = _useState10[0],
    setSaving = _useState10[1];
  const _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    error = _useState12[0],
    setError = _useState12[1];
  // A second click before Save re-renders disabled would make the setup twice.
  const savingNow = useRef(false);
  const startFrom = id => {
    var _model$name;
    const model = modelSetupFor(all, id);
    setTypeId(id);
    setStartedFrom((_model$name = model === null || model === void 0 ? void 0 : model.name) !== null && _model$name !== void 0 ? _model$name : null);
    setDraft(newSetupDraftFrom(model, methods));
    setError('');
  };
  useEffect(() => {
    var _ref2, _types$find$id, _types$find, _types$;
    if (!open) return;
    // Each opening starts again, on the type the list shows when it shows one.
    setName('');
    setNameTouched(false);
    startFrom((_ref2 = (_types$find$id = (_types$find = types.find(type => sameId(type.id, openOnType))) === null || _types$find === void 0 ? void 0 : _types$find.id) !== null && _types$find$id !== void 0 ? _types$find$id : (_types$ = types[0]) === null || _types$ === void 0 ? void 0 : _types$.id) !== null && _ref2 !== void 0 ? _ref2 : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!enabled || !draft) return null;
  const set = (key, value) => {
    setError('');
    setDraft(current => _objectSpread(_objectSpread({}, current), {}, {
      [key]: value
    }));
  };

  // What's sent is what's shown: a source or AP Push the plan doesn't include is dropped, and AP Push waits for the
  // plan, so a value taken from the model setup never goes out locked.
  const sourceLocked = sourceChoice(draft.billSource, NOTHING_HELD, allows).locked;
  const billSource = sourceLocked ? null : draft.billSource;
  const apPush = apPushChoice(NOTHING_HELD, allows);
  const isApPushEnabled = isAppfolio && draft.isApPushEnabled && !apPush.locked;
  const trimmed = name.trim();
  const nameError = nameTouched && !trimmed ? 'Give the setup a name.' : '';
  const canSave = !saving && !!trimmed && !!typeId;
  const handleClose = () => {
    if (saving) return;
    onClose === null || onClose === void 0 || onClose();
  };
  const handleSave = async () => {
    if (savingNow.current || !canSave) return;
    savingNow.current = true;
    const body = {
      name: trimmed,
      utilityTypeId: typeId,
      billSource,
      billToResidents: true,
      allocationMethod: draft.allocationMethod,
      billFrequencyType: draft.billFrequencyType,
      isApPushEnabled
    };
    setSaving(true);
    setError('');
    try {
      var _resp$response;
      const resp = await api.UtilitySetups.createSetup({
        companyId,
        draft: body
      }, NO_OPTIONS);
      // Made, whatever the answer holds: every screen reads the company's setups again.
      announceSetupChange(null);
      onCreated === null || onCreated === void 0 || onCreated((_resp$response = resp === null || resp === void 0 ? void 0 : resp.response) !== null && _resp$response !== void 0 ? _resp$response : null, trimmed);
    } catch (err) {
      setError(messageOf(err, "Couldn't make the setup."));
    } finally {
      savingNow.current = false;
      setSaving(false);
    }
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: !!open,
    handleClose: handleClose,
    variant: "middle",
    title: "New setup",
    leftButton: /*#__PURE__*/React.createElement(Button, {
      onClick: handleClose,
      variant: "text",
      disabled: saving
    }, "Cancel"),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      onClick: handleSave,
      size: "small",
      variant: "contained",
      disabled: !canSave
    }, "Save")
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editor,
    "data-testid": "utility-setup-creator"
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.twin
  }, /*#__PURE__*/React.createElement(TextField, {
    id: "new-setup-name",
    size: "small",
    label: "Setup name",
    value: name,
    onChange: event => {
      setError('');
      setName(event.target.value);
    },
    onBlur: () => setNameTouched(true),
    inputProps: {
      maxLength: MAX_SETUP_NAME_LENGTH
    },
    error: !!nameError,
    helperText: nameError || "Unique among your company's setups.",
    disabled: saving
  }), /*#__PURE__*/React.createElement(TextField, {
    select: true,
    id: "new-setup-type",
    size: "small",
    label: "Utility type",
    value: typeId,
    onChange: event => startFrom(event.target.value),
    helperText: startedFrom ? "Starts from ".concat(startedFrom, "'s settings.") : ' ',
    disabled: saving
  }, types.map(type => /*#__PURE__*/React.createElement(MenuItem, {
    key: type.id,
    value: type.id
  }, type.name)))), /*#__PURE__*/React.createElement("div", {
    className: classes.editorGroup
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    component: "div",
    className: classes.heading,
    id: "new-setup-source"
  }, "How do bills reach Livable?", /*#__PURE__*/React.createElement(Tip, {
    label: "How bills reach Livable",
    text: SETUP_TIPS.source
  })), /*#__PURE__*/React.createElement(RadioGroup, {
    "aria-labelledby": "new-setup-source",
    value: billSource !== null && billSource !== void 0 ? billSource : '',
    onChange: event => set('billSource', event.target.value),
    className: classes.cards
  }, SOURCE_CHOICES.map(choice => {
    const _sourceChoice = sourceChoice(choice.value, NOTHING_HELD, allows),
      needs = _sourceChoice.needs,
      locked = _sourceChoice.locked;
    return /*#__PURE__*/React.createElement(SourceCard, {
      key: choice.value,
      choice: choice,
      selected: billSource === choice.value,
      locked: locked || saving,
      needs: needs,
      where: "New setup",
      classes: classes
    });
  }), /*#__PURE__*/React.createElement(MailCard, {
    classes: classes
  })), billSource === 'Email' && /*#__PURE__*/React.createElement(IntakeAddress, {
    className: classes.intakeBox
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.editorGroup
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.heading
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Switch, {
      checked: true,
      disabled: true
    }),
    label: "Bill to residents"
  }), /*#__PURE__*/React.createElement(Tip, {
    label: "Bill to residents",
    text: SETUP_TIPS.billToResidents
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "Turning it off comes in Phase 2.")), /*#__PURE__*/React.createElement("div", {
    className: classes.twin
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editorGroup
  }, /*#__PURE__*/React.createElement(TextField, {
    select: true,
    id: "new-setup-method",
    size: "small",
    label: "Allocation method",
    value: draft.allocationMethod,
    onChange: event => set('allocationMethod', event.target.value),
    disabled: saving
  }, methods.map(method => /*#__PURE__*/React.createElement(MenuItem, {
    key: method,
    value: method
  }, methodLabel(method)))), SQUARE_FOOTAGE_METHODS.includes(draft.allocationMethod) && /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "p"
  }, "Each property needs its square footage set.")), /*#__PURE__*/React.createElement("div", {
    className: classes.editorGroup
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.heading
  }, /*#__PURE__*/React.createElement(TextField, {
    select: true,
    id: "new-setup-frequency",
    size: "small",
    label: "Bill frequency",
    value: draft.billFrequencyType,
    onChange: event => set('billFrequencyType', event.target.value),
    disabled: saving,
    className: classes.grow
  }, FREQUENCY_CHOICES.map(frequency => /*#__PURE__*/React.createElement(MenuItem, {
    key: frequency,
    value: frequency
  }, frequencyLabel(frequency)))), /*#__PURE__*/React.createElement(Tip, {
    label: "Bill frequency",
    text: SETUP_TIPS.frequency
  })))), isAppfolio && /*#__PURE__*/React.createElement("div", {
    className: classes.heading
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Switch, {
      checked: isApPushEnabled,
      onChange: event => set('isApPushEnabled', event.target.checked),
      disabled: apPush.locked || saving
    }),
    label: "AP Push"
  }), /*#__PURE__*/React.createElement(Tip, {
    label: "AP Push",
    text: SETUP_TIPS.apPush
  }), apPush.needs ? /*#__PURE__*/React.createElement(Needs, {
    tier: apPush.needs,
    where: "New setup",
    className: classes.needs
  }) : null, apPush.planUnknown ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    component: "span"
  }, "Turning it on waits for the company's plan.") : null), /*#__PURE__*/React.createElement("div", {
    className: classes.impact,
    role: "status"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    component: "p"
  }, "No utility follows a new setup until you link one to it, from its Setup section.")), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    onClose: () => setError('')
  }, error)));
});